<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import QRCode from 'qrcode';

import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatvidaWhatsappChannel from 'dashboard/api/channel/chatvidaWhatsappChannel';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';

const SESSION_POLL_INTERVAL = 3000;
const SESSION_STATUS = {
  SCAN_QR_CODE: 'SCAN_QR_CODE',
  WORKING: 'WORKING',
  FAILED: 'FAILED',
};

const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const inboxName = ref('');
const inboxId = ref(null);
const sessionStatus = ref(null);
const qrCodeImage = ref('');
const phoneNumber = ref('');
const isCreating = ref(false);
let pollTimer = null;

const v$ = useVuelidate({ inboxName: { required } }, { inboxName });

const isPairing = computed(() => Boolean(inboxId.value));
const isConnected = computed(
  () => sessionStatus.value === SESSION_STATUS.WORKING
);
const hasFailed = computed(() => sessionStatus.value === SESSION_STATUS.FAILED);

const stopPolling = () => {
  clearTimeout(pollTimer);
  pollTimer = null;
};

onBeforeUnmount(stopPolling);

const renderQrCode = async value => {
  qrCodeImage.value = await QRCode.toDataURL(value, { width: 264, margin: 2 });
};

const applySession = async ({
  status,
  qr_code: qrCode,
  phone_number: number,
}) => {
  sessionStatus.value = status;
  phoneNumber.value = number || '';
  if (qrCode) await renderQrCode(qrCode);
};

const goToAgentsStep = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      accountId: accountId.value,
      inbox_id: inboxId.value,
    },
  });
};

const pollSession = async () => {
  try {
    const { data } = await ChatvidaWhatsappChannel.getSession(inboxId.value);
    await applySession(data);
  } catch (error) {
    // Uma consulta que falha nao interrompe o pareamento: o WAHA pode estar
    // reiniciando a sessao. A proxima tentativa cobre.
  }

  if (isConnected.value) {
    stopPolling();
    goToAgentsStep();
    return;
  }

  pollTimer = setTimeout(pollSession, SESSION_POLL_INTERVAL);
};

const createChannel = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isCreating.value = true;
  try {
    const { data } = await ChatvidaWhatsappChannel.create({
      name: inboxName.value.trim(),
    });
    inboxId.value = data.id;
    await applySession(data);
    pollTimer = setTimeout(pollSession, SESSION_POLL_INTERVAL);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.API.ERROR_MESSAGE')
    );
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="p-6 w-full h-full col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.DESC')"
    />
    <form
      v-if="!isPairing"
      class="flex flex-col mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="inboxName"
        :label="$t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.INBOX_NAME.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.INBOX_NAME.PLACEHOLDER')
        "
        :message="
          v$.inboxName.$error
            ? $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.INBOX_NAME.ERROR')
            : ''
        "
        :message-type="v$.inboxName.$error ? 'error' : 'info'"
        @blur="v$.inboxName.$touch"
      />
      <p class="mt-2 mb-6 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.INBOX_NAME.HELP') }}
      </p>
      <Button
        type="submit"
        solid
        blue
        class="w-fit"
        :is-loading="isCreating"
        :disabled="isCreating"
        :label="$t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.SUBMIT_BUTTON')"
      />
    </form>

    <div v-else class="flex flex-col items-center text-center">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.PAIRING.TITLE') }}
      </h2>
      <p class="mt-2 mb-6 max-w-md text-sm leading-relaxed text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.PAIRING.DESCRIPTION') }}
      </p>

      <div
        class="flex justify-center items-center rounded-2xl border border-n-weak bg-n-solid-2 size-[19rem]"
      >
        <img
          v-if="qrCodeImage && !isConnected"
          :src="qrCodeImage"
          :alt="$t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.PAIRING.QR_ALT')"
          class="rounded-lg"
        />
        <Icon
          v-else-if="isConnected"
          icon="i-lucide-circle-check"
          class="text-n-teal-10 size-16"
        />
        <Icon
          v-else-if="hasFailed"
          icon="i-lucide-triangle-alert"
          class="text-n-amber-10 size-16"
        />
        <Spinner v-else class="size-8" />
      </div>

      <p v-if="isConnected" class="mt-6 text-sm font-medium text-n-slate-12">
        {{
          $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.PAIRING.CONNECTED', {
            phoneNumber,
          })
        }}
      </p>
      <p v-else-if="hasFailed" class="mt-6 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.PAIRING.FAILED') }}
      </p>
      <p v-else class="mt-6 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.CHATVIDA_WHATSAPP.PAIRING.WAITING') }}
      </p>
    </div>
  </div>
</template>
